/**
 * Shared file-type helpers for the common uploader and its adapters.
 * MIME labels here mirror the backend's grouped sets in
 * `apps/files/validation.py` and the `ACCEPT_BY_USE_CASE` picker map.
 */

export interface UploadedFileItem {
  uuid: string
  file_name: string
  file_url?: string | null
  mime_type?: string | null
}

/**
 * The loose response shape the file-upload hook resolves with. The backend has
 * historically returned either snake_case or PascalCase/camelCase keys, so the
 * contract is deliberately permissive.
 */
export interface UploadFileResult {
  uuid?: string
  UUID?: string
  file_name?: string
  fileName?: string
  file_url?: string | null
  mime_type?: string | null
  [key: string]: unknown
}

/**
 * Normalise a raw upload-hook response into the canonical {@link UploadedFileItem}
 * envelope. This is the single source of truth for reconciling the hook's
 * inconsistent key casing (`uuid`/`UUID`, `file_name`/`fileName`); callers that
 * need a stable shape (e.g. the multi-file uploader) should map through here
 * rather than re-deriving the fallbacks inline. `fallback` supplies name/mime
 * from the local File when the response omits them.
 */
export function toUploadedFileItem(
  result: UploadFileResult,
  fallback?: { name?: string; type?: string },
): UploadedFileItem {
  return {
    uuid: (result.uuid ?? result.UUID ?? '') as string,
    file_name: (result.file_name ?? result.fileName ?? fallback?.name ?? '') as string,
    file_url: (result.file_url ?? null) as string | null,
    mime_type: (result.mime_type ?? fallback?.type ?? null) as string | null,
  }
}

const MIME_LABEL: Record<string, string> = {
  'image/jpeg': 'JPG',
  'image/png': 'PNG',
  'image/heic': 'HEIC',
  'image/heif': 'HEIC',
  'image/webp': 'WEBP',
  'image/gif': 'GIF',
  'application/pdf': 'PDF',
  'application/msword': 'DOC',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'DOCX',
  'application/vnd.ms-powerpoint': 'PPT',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'PPTX',
  'application/vnd.ms-excel': 'XLS',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'XLSX',
  'text/plain': 'TXT',
  'text/csv': 'CSV',
  'video/mp4': 'MP4',
  'video/quicktime': 'MOV',
  'video/x-msvideo': 'AVI',
  'video/x-ms-wmv': 'WMV',
}

// Wildcard subtypes (e.g. "image/*", "video/*") name a whole family, not a
// single format - render a friendly plural rather than a bare "*".
const WILDCARD_LABEL: Record<string, string> = {
  image: 'Images',
  video: 'Videos',
  audio: 'Audio',
}

/**
 * Turn an `accept` string into a readable, de-duped list e.g. "PNG, JPG, PDF".
 *
 * Wildcard family labels ("Images"/"Videos"/"Audio") are the only user-facing
 * copy here; pass a translate fn (`t`) to localise them. Without it the English
 * fallback is used, so non-i18n call sites keep working unchanged.
 */
export function humanizeAccept(
  accept: string,
  t?: (key: string, defaultValue: string) => string,
): string {
  const seen = new Set<string>()
  const labels: string[] = []
  for (const raw of accept.split(',')) {
    const mime = raw.trim()
    if (!mime || mime === '*/*') continue
    let label: string
    if (mime.endsWith('/*')) {
      const top = mime.slice(0, -2)
      const fallback = WILDCARD_LABEL[top] || top.toUpperCase()
      label = t ? t(`fileTypes.${top}`, fallback) : fallback
    } else {
      label =
        MIME_LABEL[mime] ||
        (mime.startsWith('.')
          ? mime.slice(1).toUpperCase()
          : mime.split('/').pop()?.toUpperCase()) ||
        mime
    }
    if (!seen.has(label)) {
      seen.add(label)
      labels.push(label)
    }
  }
  return labels.join(', ')
}

export function extensionLabel(name: string | undefined): string | undefined {
  if (!name) return undefined
  const dot = name.lastIndexOf('.')
  if (dot < 0 || dot === name.length - 1) return undefined
  return name.slice(dot + 1).toUpperCase()
}

/** Short type caption for a file row, e.g. "PDF", "JPG", "XLSX". */
export function fileKindLabel(
  mime: string | null | undefined,
  name: string | undefined,
): string {
  if (mime && MIME_LABEL[mime]) return MIME_LABEL[mime]
  const ext = extensionLabel(name)
  if (ext) return ext
  if (mime) return mime.split('/').pop()?.toUpperCase() || 'FILE'
  return 'FILE'
}

export function isImageValue(
  mime: string | null | undefined,
  name: string | undefined,
): boolean {
  if (mime) return mime.startsWith('image/')
  const ext = (extensionLabel(name) || '').toLowerCase()
  return ['png', 'jpg', 'jpeg', 'webp', 'gif'].includes(ext)
}
